import { sourceCoverageNotes } from './source-coverage';
import { recoveryWarnings, recoveryPending } from './recovery-warning';
import { PerformanceRequest, WorkloadReport } from './types';

type Text = (key: string, values?: Record<string, unknown>) => string;
export interface PrintTable {
  title?: string;
  headers: string[];
  rows: string[][];
  note?: string;
}
export interface PrintSection {
  title: string;
  notes: string[];
  tables: PrintTable[];
}
export interface PerformancePrintDocument {
  title: string;
  metadata: Array<[string, string]>;
  warnings: string[];
  sections: PrintSection[];
}
export interface PrintReportInput {
  report: WorkloadReport;
  request: PerformanceRequest;
  agencyId: number;
  agencyName?: string;
  workflowName: string;
  zone: string;
  locale?: string;
  responseClock?: 'elapsed' | 'business';
  originLabel: (origin: string) => string;
  statusLabel: (status: string) => string;
}

export function canPrintPerformance(report: WorkloadReport, request: PerformanceRequest) {
  return (
    Number.isFinite(Date.parse(report.generatedAt)) &&
    Number.isFinite(Date.parse(request.from)) &&
    Number.isFinite(Date.parse(request.to)) &&
    Date.parse(report.range?.from) === Date.parse(request.from) &&
    Date.parse(report.range?.to) === Date.parse(request.to) &&
    Date.parse(request.from) < Date.parse(request.to) &&
    Boolean(report.conversion) === (request.view === 'conversion') &&
    (request.view !== 'daily' || Boolean(report.closings)) &&
    (request.view !== 'conversion' ||
      Boolean(
        report.conversion &&
          report.conversion.grain === (request.grain ?? 'advisor') &&
          Date.parse(report.conversion.cutoff) === Date.parse(request.cutoff ?? request.to),
      ))
  );
}

export function printDate(value: string | null | undefined, zone: string, locale?: string) {
  if (!value || !Number.isFinite(Date.parse(value))) return '—';
  return new Date(value).toLocaleString(locale, { timeZone: zone });
}
const localDay = (value: number, zone: string) => {
  const parts = new Intl.DateTimeFormat('en', {
    timeZone: zone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date(value));
  return ['year', 'month', 'day'].map((type) => parts.find((part) => part.type === type)!.value).join('-');
};

/** Empty calendar dates remain unknown, never a zero or an assumed day off. */
export function printClosingDates(from: string, to: string, zone: string, recordedDates: string[]) {
  const first = localDay(Date.parse(from), zone);
  const last = localDay(Date.parse(to) - 1, zone);
  const dates = new Set(recordedDates);
  for (
    let day = Date.parse(`${first}T00:00:00Z`), end = Date.parse(`${last}T00:00:00Z`);
    day <= end && dates.size <= 64;
    day += 86400000
  ) dates.add(new Date(day).toISOString().slice(0, 10));
  return [...dates].sort();
}

export function buildPerformancePrintDocument(input: PrintReportInput, text: Text): PerformancePrintDocument {
  const { report, request, zone, locale } = input;
  const date = (value?: string | null) => printDate(value, zone, locale);
  const noData = text('unassigned');
  const minutes = (value: number | null | undefined) =>
    value === null || value === undefined ? '—' : (value / 60).toLocaleString(locale, { maximumFractionDigits: 1 });
  const percent = (value: number | null) =>
    value === null ? '—' : `${(value * 100).toLocaleString(locale, { maximumFractionDigits: 1 })}%`;
  const origin = (value: string | null) => (value ? input.originLabel(value) : noData);
  const dimensions = (advisor?: string | null, team?: string | number | null, source?: string | null) => [
    advisor ?? noData,
    team === null || team === undefined ? noData : String(team),
    origin(source ?? null),
  ];
  const groupLabels = [text('workloadAdvisor'), text('workloadTeam'), text('origin')];
  const responseGroups = report.responses?.groups ?? [];
  const conversionFilters = report.conversion?.filters ?? [];
  const closingGroups = report.closings?.groups ?? [];
  const advisorName =
    report.advisors.find((row) => row.agentUserId === request.advisor)?.agentName ??
    responseGroups.find((row) => row.advisor === request.advisor)?.advisorName ??
    conversionFilters.find((row) => row.advisor === request.advisor)?.advisorName ??
    closingGroups.find((row) => row.advisor === request.advisor)?.advisorName;
  const teamName =
    report.advisors.find((row) => row.teamId === request.team)?.teamName ??
    responseGroups.find((row) => row.team === request.team)?.teamName ??
    conversionFilters.find((row) => row.team === request.team)?.teamName ??
    closingGroups.find((row) => row.team === request.team)?.teamName;
  const document: PerformancePrintDocument = {
    title: `${text('print.title')} · ${text(request.view ?? 'workload')}`,
    metadata: [
      [text('agency'), input.agencyName ?? String(input.agencyId)],
      [text('workflow'), input.workflowName],
      [text('print.view'), text(request.view ?? 'workload')],
      [text('workloadAdvisor'), request.advisor ? (advisorName ?? request.advisor) : text('all')],
      [text('workloadTeam'), request.team ? (teamName ?? String(request.team)) : text('all')],
      [text('origin'), request.origin ? origin(request.origin) : text('all')],
      [text('print.fromInclusive'), date(report.range.from)],
      [text('print.toExclusive'), date(report.range.to)],
      [text('print.zone'), zone],
      [text('print.generated'), date(report.generatedAt)],
      [text('print.sourceSince'), date(report.sourceSince)],
    ],
    warnings: [
      text('captureUnverified'),
      ...recoveryWarnings(report, text),
      ...sourceCoverageNotes(report.source, text),
    ],
    sections: [],
  };
  if (recoveryPending(report)) return document;
  if (report.capture.gapAt || report.capture.state !== 'observing') document.warnings.push(text('captureGap'));
  const section = (title: string, notes: string[], tables: PrintTable[]) =>
    document.sections.push({ title, notes, tables });
  const table = (headers: string[], rows: string[][], note?: string, title?: string): PrintTable => ({
    headers,
    rows,
    note,
    title,
  });
  const paged = (count: number, partial: boolean) =>
    text(partial ? 'print.detailPage' : 'print.detailLoaded', { count });
  const metricTable = (values: Array<[string, string]>, title?: string) =>
    table([text('print.metric'), text('print.value')], values, undefined, title);

  if (report.conversion) {
    const value = report.conversion;
    document.metadata.push(
      [text('conversionCutoff'), date(value.cutoff)],
      [text('print.grain'), text(value.grain === 'advisor' ? 'conversionAdvisor' : 'conversionWorkflow')],
    );
    const keys = [
      'received',
      'eligible',
      'won',
      'rate',
      'lost',
      'closed',
      'open',
      'transferred',
      'otherSuccess',
      'alreadySuccessful',
      'unknown',
    ] as const;
    const counts = (row: NonNullable<typeof value.totals>) =>
      keys.map(
        (key) => [text(`conversion_${key}`), key === 'rate' ? percent(row.rate) : String(row[key])] as [string, string],
      );
    const tables: PrintTable[] = [];
    if (value.totals && !value.truncated) tables.push(metricTable(counts(value.totals), text('print.cohortTotals')));
    if (value.truncated) document.warnings.push(text('conversionLimit', { limit: value.limit }));
    if (value.groupsTruncated) document.warnings.push(text('groupsTruncated'));
    if (!value.truncated) value.groups.forEach((row) =>
      tables.push(
        metricTable(
          counts(row),
          dimensions(
            row.advisorName ?? row.advisor ?? text('conversionWorkflow'),
            row.teamName ?? row.team,
            row.origin,
          ).join(' / '),
        ),
      ),);
    tables.push(
      table(
        [
          text('case'),
          text('workloadAdvisor'),
          text('conversionReceivedAt'),
          text('responseResult'),
          text('conversionSuccessAt'),
        ],
        value.cases.map((row) => [
          `#${row.caseId}`,
          row.advisorName ?? row.advisor ?? text('conversionWorkflow'),
          date(row.receivedAt),
          text(`conversion_${row.result}`),
          row.result === 'unknown' ? '—' : date(row.successAt),
        ]),
        paged(value.cases.length, Boolean(request.afterConversionCaseId || value.next || value.truncated)),
        text('conversionDetails'),
      ),
    );
    section(
      text('conversion'),
      [
        text(value.grain === 'advisor' ? 'conversionAdvisorHint' : 'conversionWorkflowHint'),
        text('conversionRulesHint'),
      ],
      tables,
    );
    return document;
  }

  if (report.advisorsTruncated) document.warnings.push(text('groupsTruncated'));
  section(
    text('print.assignments'),
    [text('print.assignmentHint')],
    [
      table(
        [...groupLabels, text('received')],
        report.advisors.map((row) => [
          ...dimensions(row.agentName ?? row.agentUserId, row.teamName ?? row.teamId, row.origin),
          row.received,
        ]),
      ),
    ],
  );

  if (report.responses) {
    const value = report.responses;
    const business = input.responseClock === 'business';
    document.metadata.push([text('print.responseClock'), text(business ? 'businessClock' : 'elapsedClock')]);
    if (value.groupsTruncated) document.warnings.push(text('groupsTruncated'));
    if (!value.activityAvailable) document.warnings.push(text('print.activityUnavailable'));
    if (
      Number(value.progress.partialCases) ||
      Number(value.progress.unplacedCases) ||
      Number(value.progress.updatingCases)
    ) document.warnings.push(text('responseProgress', { ...value.progress }));
    const stats = value.groups.map((row) =>
      metricTable(
        [
          [text('contactedCases'), value.activityAvailable ? row.contactedCases : '—'],
          [
            text(business ? 'businessAnswered' : 'answered'),
            business ? `${row.businessAnswered ?? '—'} / ${row.answered}` : row.answered,
          ],
          [text('meanMinutes'), minutes(business ? row.meanBusinessSeconds : row.meanSeconds)],
          [text('medianMinutes'), minutes(business ? row.medianBusinessSeconds : row.medianSeconds)],
          [text('p90Minutes'), minutes(business ? row.p90BusinessSeconds : row.p90Seconds)],
          [
            text('sla'),
            Number(business ? row.businessSlaEvaluated : row.slaEvaluated)
              ? [business ? row.businessWithinThreshold : row.withinThreshold,
                business ? row.businessSlaEvaluated : row.slaEvaluated].join(' / ')
              : '—',
          ],
          [text('closedWithoutReply'), row.closedWithoutReply],
          [text('responseUnverifiedScope'), row.uncertain],
        ],
        dimensions(row.advisorName ?? row.advisor, row.teamName ?? row.team, row.origin).join(' / '),
      ),);
    stats.push(
      table(
        [
          text('case'),
          text('waitingSince'),
          text('responseResult'),
          text('print.endedAt'),
          text(business ? 'businessMinutes' : 'elapsedMinutes'),
          text('lastObserved'),
        ],
        value.cases.map((row) => [
          `#${row.caseId}`,
          date(row.startedAt),
          text(
            row.partial || row.uncertain
              ? 'unverified'
              : row.answeredAt
                ? 'responseAnswered'
                : row.closedAt
                  ? 'closedWithoutReply'
                  : row.transferredAt
                    ? 'responseTransferred'
                    : 'unanswered',
          ),
          date(row.answeredAt ?? row.closedAt ?? row.transferredAt),
          minutes(business ? (row.partial || row.uncertain ? null : row.businessSeconds) : row.seconds),
          date(business ? row.businessMeasuredUntil : row.checkedAt),
        ]),
        paged(value.cases.length, Boolean(request.afterResponseCaseId || value.next)),
        text('responseDetails'),
      ),
    );
    if (value.issues.length) stats.push(
      table(
        [text('case'), text('lastObserved')],
        value.issues.map((row) => [`#${row.caseId}`, date(row.checkedAt)]),
        text('responseIssues'),
      ),
    );
    section(
      text('responses'),
      [text('responsesHint'), text(business ? 'businessHint' : 'elapsedHint'), text('print.responseHint')],
      stats,
    );
  }

  if (report.closings) {
    const value = report.closings;
    const tables: PrintTable[] = [];
    if (value.truncated) document.warnings.push(text('dayEnd.limit', { limit: value.limit }));
    if (value.groupsTruncated) document.warnings.push(text('groupsTruncated'));
    if (value.progress.waiting || value.progress.incomplete || value.progress.truncated) document.warnings.push(text('dayEnd.incomplete'));
    if (Date.parse(request.from) < Date.parse(value.retainedSince)) document.warnings.push(text('dayEnd.retention'));
    if (!value.truncated && value.dailyGroups) {
      const groups = new Map(value.dailyGroups.map((row) => [row.date, row]));
      const days = printClosingDates(request.from, request.to, zone, [...groups.keys()]);
      tables.push(
        table(
          [
            text('print.day'),
            text('print.closingZones'),
            text('dayEnd.evaluated'),
            text('dayEnd.pending'),
            text('print.distinctPendingCases'),
            text('dayEnd.unknown'),
          ],
          days.map((day) => {
            const row = groups.get(day);
            return [
              day,
              row ? row.zones.join(', ') : '—',
              row ? String(row.evaluated) : text('print.noRecords'),
              row ? String(row.pending) : '—',
              row ? String(row.pendingCases) : '—',
              row ? String(row.unknown) : '—',
            ];
          }),
          text('print.dailyHint'),
          text('print.allClosings'),
        ),
      );
    } else document.warnings.push(text('print.dailyUnavailable'));
    if (value.totals && !value.truncated) tables.push(
      metricTable(
        [
          [text('dayEnd.evaluated'), String(value.totals.evaluated)],
          [text('dayEnd.pending'), String(value.totals.pending)],
          [text('print.distinctPendingCases'), String(value.totals.pendingCases)],
          [text('dayEnd.unknown'), String(value.totals.unknown)],
        ],
        text('print.periodClosings'),
      ),
    );
    tables.push(
      table(
        [
          text('workloadAdvisor'),
          text('workloadTeam'),
          text('dayEnd.evaluated'),
          text('dayEnd.pending'),
          text('dayEnd.unknown'),
        ],
        value.groups.map((row) => [
          row.advisorName ?? row.advisor,
          row.teamName ?? String(row.team ?? '—'),
          String(row.evaluated),
          String(row.pending),
          String(row.unknown),
        ]),
      ),
    );
    const { closingDate } = request;
    tables.push(
      table(
        [text('case'), text('workloadAdvisor'), text('dayEnd.end'), text('responseResult'), text('dayEnd.checked')],
        value.cases.map((row) => [
          `#${row.caseId}`,
          row.advisorName ?? row.advisor,
          `${printDate(row.closeAt, row.zone, locale)} (${row.zone})`,
          text(`dayEnd.${row.uncertain ? 'unknown' : row.pending ? 'pending' : 'clear'}`),
          date(row.checkedAt),
        ]),
        [
          paged(value.cases.length, Boolean(request.closingOffset || value.nextOffset !== null || value.truncated)),
          closingDate ? text('print.closingDetailDate', { date: closingDate }) : '',
        ]
          .filter(Boolean)
          .join(' '),
        text('print.closingDetails'),
      ),
    );
    section(
      text('dayEnd.title'),
      [text('dayEnd.hint'), text('dayEnd.progress', { ...value.progress }), text('print.repeatedCases')],
      tables,
    );
  }

  const currentTables = [
    table(
      [...groupLabels, text('portfolio'), text('needsAgent')],
      report.advisors.map((row) => [
        ...dimensions(row.agentName ?? row.agentUserId, row.teamName ?? row.teamId, row.origin),
        row.portfolio,
        row.needsAgent,
      ]),
    ),
  ];
  if (report.responses) currentTables.push(
    table(
      [...groupLabels, text('unanswered'), text('overdueElapsed')],
      report.responses.groups.map((row) => [
        ...dimensions(row.advisorName ?? row.advisor, row.teamName ?? row.team, row.origin),
        row.pending,
        row.overdue,
      ]),
    ),
  );
  currentTables.push(
    table(
      [text('case'), text('status'), text('pendingSince'), text('lastObserved')],
      report.cases.map((row) => [
        `#${row.caseId}`,
        input.statusLabel(row.status),
        date(row.needsAgentSince),
        date(row.observedAt),
      ]),
      paged(report.cases.length, Boolean(request.afterCaseId || report.nextCaseId)),
      text('currentCases'),
    ),
  );
  section(text('print.currentState'), [text('print.currentHint', { at: date(report.generatedAt) })], currentTables);
  return document;
}
